// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { SortOrderOptions } from "@conklin/MobiusOverrides/DataTable/DataTable";
import { sharedDateTimeOptions } from "@conklin/XDL/constants";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getQuotesDataView } from "@insite/client-framework/Store/Data/Quotes/QuotesSelector";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import updateSearchFields from "@insite/client-framework/Store/Pages/RfqMyQuotes/Handlers/UpdateSearchFields";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import DataTable, { DataTableProps } from "@insite/mobius/DataTable";
import DataTableBody, { DataTableBodyProps } from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell, { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import DataTableHead, { DataTableHeadProps } from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow, { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    session: state.context.session,
    quotesDataView: getQuotesDataView(state, state.pages.rfqMyQuotes.getQuotesParameter),
    rfqQuoteDetailsPageUrl: getPageLinkByPageType(state, "RfqQuoteDetailsPage")?.url,
    language: state.context.session.language,
    // XNG-Changes
    getQuotesParameter: state.pages.rfqMyQuotes.getQuotesParameter,
});

// XNG-Changes
const mapDispatchToProps = {
    updateSearchFields,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface RfqMyQuotesTableStyles {
    wrapper?: InjectableCss;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    noResultsContainer?: InjectableCss;
    noResultsText?: TypographyProps;
    dataTable?: DataTableProps;
    dataTableHead?: DataTableHeadProps;
    dataTableHeaders?: DataTableHeaderProps;
    dataTableBody?: DataTableBodyProps;
    dataTableRow?: DataTableRowProps;
    quoteNumberHeader?: DataTableHeaderProps;
    typeHeader?: DataTableHeaderProps;
    statusHeader?: DataTableHeaderProps;
    orderDateHeader?: DataTableHeaderProps;
    expirationDateHeader?: DataTableHeaderProps;
    salesRepHeader?: DataTableHeaderProps;
    userHeader?: DataTableHeaderProps;
    customerHeader?: DataTableHeaderProps;
    shipToHeader?: DataTableHeaderProps;
    quoteNumberCell?: DataTableCellProps;
    quoteLink?: LinkPresentationProps;
    typeCell?: DataTableCellProps;
    statusCell?: DataTableCellProps;
    orderDateCell?: DataTableCellProps;
    expirationDateCell?: DataTableCellProps;
    salesRepCell?: DataTableCellProps;
    userCell?: DataTableCellProps;
    customerCell?: DataTableCellProps;
    shipToCell?: DataTableCellProps;
}

export const rfqMyQuotesTableStyles: RfqMyQuotesTableStyles = {
    wrapper: {
        css: css`
            overflow: auto;
        `,
    },
    centeringWrapper: {
        css: css`
            height: 300px;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
    noResultsContainer: {
        css: css`
            text-align: center;
            padding: 20px;
        `,
    },
    noResultsText: {
        size: 16,
        weight: 600,
    },
    customerCell: {
        typographyProps: {
            ellipsis: true,
            css: css`
                display: block;
                max-width: 200px;
            `,
        },
    },
    shipToCell: {
        typographyProps: {
            ellipsis: true,
            css: css`
                display: block;
                max-width: 200px;
            `,
        },
    },
};

const styles = rfqMyQuotesTableStyles;

const RfqMyQuotesTable = ({
    session,
    quotesDataView,
    rfqQuoteDetailsPageUrl,
    language,
    // XNG-Changes
    getQuotesParameter,
    updateSearchFields,
}: Props) => {
    const quotes = quotesDataView.value;
    if (!quotes) {
        return (
            <StyledWrapper {...styles.centeringWrapper}>
                <LoadingSpinner {...styles.spinner}></LoadingSpinner>
            </StyledWrapper>
        );
    }

    if (quotes.length === 0) {
        return (
            <StyledWrapper {...styles.noResultsContainer}>
                <Typography {...styles.noResultsText}>{translate("No quotes found")}</Typography>
            </StyledWrapper>
        );
    }

    const rows = quotes.map(quote => ({
        id: quote.id,
        quoteNumber: quote.quoteNumber,
        type: quote.typeDisplay,
        status: quote.statusDisplay,
        orderDate: quote.orderDate
            ? getLocalizedDateTime({
                  dateTime: new Date(quote.orderDate),
                  language,
                  /**
                   * XNG-Changes:
                   * OOTB issue: Date Format fix
                   */
                  options: {
                      ...sharedDateTimeOptions,
                  },
              })
            : "",
        expirationDate: quote.expirationDate
            ? getLocalizedDateTime({
                  dateTime: new Date(quote.expirationDate),
                  language,
                  /**
                   * XNG-Changes:
                   * OOTB issue: Date Format fix
                   */
                  options: {
                      ...sharedDateTimeOptions,
                  },
              })
            : "",
        salesRep: quote.salespersonName,
        user: quote.userName,
        // Xng-Changes
        customer: quote.properties?.companyName || quote.billTo?.companyName,
        shipTo: quote.shipToFullAddress,
    }));

    // XNG-Changes: adding sort to quotes table
    const headerClick = (sortField: string) => {
        const sort = getQuotesParameter.sort === sortField ? `${sortField} DESC` : sortField;
        updateSearchFields({ sort });
    };
    // XNG-Changes: adding sort to quotes table
    const sorted = (sortField: string) => {
        let sorted: SortOrderOptions | undefined;
        if (getQuotesParameter.sort === sortField) {
            sorted = SortOrderOptions.ascending;
        } else if (getQuotesParameter.sort === `${sortField} DESC`) {
            sorted = SortOrderOptions.descending;
        }
        return sorted;
    };

    return (
        <StyledWrapper {...styles.wrapper}>
            <DataTable {...styles.dataTable} data-test-selector="rfqMyQuotes_quoteTable">
                <DataTableHead {...styles.dataTableHead}>
                    <DataTableHeader
                        {...styles.quoteNumberHeader}
                        title={translate("Quote Number")}
                        // XNG-Changes: adding sort to quotes table
                        sorted={sorted("QuoteNumber")}
                        onSortClick={() => headerClick("QuoteNumber")}
                    >
                        {translate("Quote #")}
                    </DataTableHeader>
                    <DataTableHeader tight {...styles.typeHeader}>
                        {translate("Type")}
                    </DataTableHeader>
                    <DataTableHeader
                        tight
                        {...styles.statusHeader}
                        // XNG-Changes: adding sort to quotes table
                        sorted={sorted("Status")}
                        onSortClick={() => headerClick("Status")}
                    >
                        {translate("Status")}
                    </DataTableHeader>
                    <DataTableHeader
                        tight
                        {...styles.orderDateHeader}
                        // XNG-Changes: adding sort to quotes table
                        sorted={sorted("RequestedDate")}
                        onSortClick={() => headerClick("RequestedDate")}
                    >
                        {translate("Requested")}
                    </DataTableHeader>
                    <DataTableHeader
                        tight
                        {...styles.expirationDateHeader}
                        // XNG-Changes: adding sort to quotes table
                        sorted={sorted("ExpirationDate")}
                        onSortClick={() => headerClick("ExpirationDate")}
                    >
                        {translate("Expires")}
                    </DataTableHeader>
                    {session.isSalesPerson && (
                        <>
                            <DataTableHeader
                                tight
                                {...styles.salesRepHeader}
                                // XNG-Changes: adding sort to quotes table
                                sorted={sorted("SalesPersonName")}
                                onSortClick={() => headerClick("SalesPersonName")}
                            >
                                {translate("Sales Rep")}
                            </DataTableHeader>
                            <DataTableHeader
                                tight
                                {...styles.userHeader}
                                // XNG-Changes: adding sort to quotes table
                                sorted={sorted("UserName")}
                                onSortClick={() => headerClick("UserName")}
                            >
                                {translate("User")}
                            </DataTableHeader>
                        </>
                    )}
                    <DataTableHeader tight {...styles.customerHeader}>
                        {translate("Customer")}
                    </DataTableHeader>
                    {!session.isSalesPerson && (
                        <DataTableHeader tight {...styles.shipToHeader} title={translate("Fulfillment")}>
                            {translate("Ship To/Pick Up")}
                        </DataTableHeader>
                    )}
                </DataTableHead>
                <DataTableBody {...styles.dataTableBody}>
                    {rows.map(
                        ({
                            id,
                            quoteNumber,
                            type,
                            status,
                            orderDate,
                            expirationDate,
                            salesRep,
                            user,
                            customer,
                            shipTo,
                        }) => (
                            <DataTableRow
                                key={id}
                                {...styles.dataTableRow}
                                data-test-selector={`rfqMyQuotes_quoteLine_${id}`}
                            >
                                <DataTableCell {...styles.quoteNumberCell} {...{ header: translate("Quote #") }}>
                                    <Link
                                        {...styles.quoteLink}
                                        href={`${rfqQuoteDetailsPageUrl}?quoteId=${id}`}
                                        data-test-selector="link"
                                        aria-label={quoteNumber}
                                    >
                                        {quoteNumber}
                                    </Link>
                                </DataTableCell>
                                <DataTableCell {...styles.typeCell} {...{ header: translate("Type") }}>
                                    {type}
                                </DataTableCell>
                                <DataTableCell {...styles.statusCell} {...{ header: translate("Status") }}>
                                    {status}
                                </DataTableCell>
                                <DataTableCell {...styles.orderDateCell} {...{ header: translate("Requested") }}>
                                    {orderDate}
                                </DataTableCell>
                                <DataTableCell {...styles.expirationDateCell} {...{ header: translate("Expires") }}>
                                    {expirationDate}
                                </DataTableCell>
                                {session.isSalesPerson && (
                                    <>
                                        <DataTableCell {...styles.salesRepCell} {...{ header: translate("Sales Rep") }}>
                                            {salesRep}
                                        </DataTableCell>
                                        <DataTableCell
                                            {...styles.userCell}
                                            data-test-selector="user"
                                            {...{ header: translate("User") }}
                                        >
                                            {user}
                                        </DataTableCell>
                                    </>
                                )}
                                <DataTableCell {...styles.customerCell} {...{ header: translate("Customer") }}>
                                    {customer}
                                </DataTableCell>
                                {!session.isSalesPerson && (
                                    <DataTableCell
                                        {...styles.shipToCell}
                                        {...{ header: translate("Ship To/Pick Up") }}
                                        // XNG-Changes
                                        data-test-selector="requestQuoteLine_shipTo"
                                    >
                                        {shipTo}
                                    </DataTableCell>
                                )}
                            </DataTableRow>
                        ),
                    )}
                </DataTableBody>
            </DataTable>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(RfqMyQuotesTable),
    definition: {
        group: "RFQ My Quotes",
        displayName: "Search Results Table",
        allowedContexts: ["RfqMyQuotesPage"],
    },
};

export default widgetModule;
